import { useEffect, useState } from 'react';
import { Download, Clock, Thermometer, Calendar, RefreshCw } from 'lucide-react';
import type { CookDetails, CookEstimate, WeatherData, SmokerSpec, MeatProperties } from '../../types';
import { WeatherService } from '../../services/weather.service';
import { CookTimeCalculator } from '../../services/calculator.service';
import { PDFGenerator } from '../../services/pdf-generator';
import { format } from 'date-fns';

interface Props {
  data: Partial<CookDetails>;
  onChange: (data: Partial<CookDetails>) => void;
  onNext: () => void;
}

export default function Results({ data }: Props) {
  const [estimate, setEstimate] = useState<CookEstimate | null>(null);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [smokerSpec, setSmokerSpec] = useState<SmokerSpec | null>(null);
  const [meatProps, setMeatProps] = useState<MeatProperties | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    calculateCookTime();
  }, []);

  const calculateCookTime = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load smoker spec
      const smokersResponse = await fetch('/config/smokers.json');
      const smokers = await smokersResponse.json();
      const smoker = smokers.find((s: SmokerSpec) => 
        `${s.manufacturer.toLowerCase()}-${s.model.toLowerCase().replace(/\s+/g, '-')}` === data.smokerModel
      );
      
      if (!smoker) {
        throw new Error('Smoker not found');
      }
      setSmokerSpec(smoker);

      // Load meat properties
      const meatsResponse = await fetch('/config/meats.json');
      const meats = await meatsResponse.json();
      const meat = meats.find((m: MeatProperties) => m.type === data.meatType);
      
      if (!meat) {
        throw new Error('Meat type not found');
      }
      
      // Apply variant overrides if specified
      let meatWithVariant = { ...meat };
      if (data.variant && meat.variants && meat.variants[data.variant]) {
        const variant = meat.variants[data.variant];
        // Apply variant overrides
        if (variant.cookRateMultiplier !== undefined) {
          meatWithVariant.empirical = {
            ...meat.empirical,
            baseCookRateHrsPerLb225F: meat.empirical.baseCookRateHrsPerLb225F * variant.cookRateMultiplier,
            cookRateHrsPerLbAt250F: meat.empirical.cookRateHrsPerLbAt250F ? meat.empirical.cookRateHrsPerLbAt250F * variant.cookRateMultiplier : undefined,
            cookRateHrsPerLbAt275F: meat.empirical.cookRateHrsPerLbAt275F ? meat.empirical.cookRateHrsPerLbAt275F * variant.cookRateMultiplier : undefined,
          };
        }
        if (variant.targetInternalF !== undefined) {
          meatWithVariant.temperatures = {
            ...meat.temperatures,
            targetInternalF: variant.targetInternalF
          };
        }
      }
      setMeatProps(meatWithVariant);

      // Fetch weather
      const weatherService = new WeatherService();
      const weatherData = await weatherService.getWeather(data.location!);
      setWeather(weatherData);

      // Load calculation constants
      const constantsResponse = await fetch('/config/calculation-constants.json');
      const constantsData = await constantsResponse.json();

      // Calculate cook time
      const calculator = new CookTimeCalculator();
      
      const result = calculator.calculate(
        data as CookDetails,
        smoker,
        meat,
        weatherData,
        constantsData
      );
      
      setEstimate(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to calculate cook time';
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!estimate || !weather || !smokerSpec || !meatProps) return;

    try {
      const pdfGen = new PDFGenerator();
      await pdfGen.generate(
        data as CookDetails,
        estimate,
        weather,
        smokerSpec,
        meatProps
      );
      
      const filename = `cook-plan-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      pdfGen.save(filename);
    } catch (err) {
      alert('Failed to generate PDF. Please try again.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-flame-600 mb-4" />
        <p className="text-smoke-600">Calculating your cook time...</p>
        <p className="text-sm text-smoke-500 mt-2">Fetching weather data and running calculations</p>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="text-center py-12">
        <p className="text-flame-600 font-semibold mb-4">{error || 'No estimate available'}</p>
        <button onClick={calculateCookTime} className="btn-primary inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  const confidenceColor =
    estimate.summary.confidenceLevel === 'HIGH'
      ? 'text-green-600'
      : estimate.summary.confidenceLevel === 'MEDIUM'
      ? 'text-yellow-600'
      : 'text-orange-600';

  return (
    <div className="space-y-6">
      {/* Key Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-flame-500 to-flame-600 text-white p-4 rounded-xl">
          <Clock className="w-6 h-6 mb-2 opacity-80" />
          <div className="text-2xl sm:text-3xl font-bold">{estimate.summary.totalTimeIncludingRestHours.toFixed(1)}h</div>
          <div className="text-sm opacity-90">Total Time</div>
        </div>
        <div className="bg-gradient-to-br from-smoke-600 to-smoke-700 text-white p-4 rounded-xl">
          <Thermometer className="w-6 h-6 mb-2 opacity-80" />
          <div className="text-2xl sm:text-3xl font-bold">{estimate.summary.weatherAdjustmentHours > 0 ? '+' : ''}{estimate.summary.weatherAdjustmentHours.toFixed(1)}h</div>
          <div className="text-sm opacity-90">Weather Impact</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="card">
        <h3 className="text-lg font-semibold text-smoke-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Cook Timeline
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-smoke-600">Start Time</span>
            <span className="font-semibold text-smoke-900">
              {estimate.timeline?.startTime ? 
                format(new Date(estimate.timeline.startTime), 'EEE, MMM d h:mm a') : 
                'N/A'
              }
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-smoke-600">Finish Time</span>
            <span className="font-semibold text-smoke-900">
              {estimate.timeline?.endTime ? 
                format(new Date(estimate.timeline.endTime), 'EEE, MMM d h:mm a') : 
                'N/A'
              }
            </span>
          </div>
          {!data.targetServingTime && (
            <p className="text-xs text-amber-600 italic pt-2">
              ⚠️ No target serving time set. Times shown are estimated from now forward. 
              Set your serving time in Cook Settings for accurate planning.
            </p>
          )}
          <div className="flex justify-between items-center pt-3 border-t border-smoke-200">
            <span className="text-smoke-600">Confidence</span>
            <span className={`font-semibold ${confidenceColor}`}>{estimate.summary.confidenceLevel} (±{estimate.summary.uncertaintyRangeHours}h)</span>
          </div>
        </div>
      </div>

      {/* Weather Info */}
      {weather && (
        <div className="card">
          <h3 className="text-lg font-semibold text-smoke-900 mb-4 flex items-center gap-2">
            <Thermometer className="w-5 h-5" />
            Weather Analysis
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Location</span>
              <span className="font-medium">{weather.location.name}</span>
            </div>
            
            {/* Show forecast analysis if available */}
            {weather.hourly && weather.hourly.length > 0 && estimate?.timeline?.startTime ? (
              <>
                <div className="pt-2 pb-1 border-t border-smoke-200">
                  <p className="text-xs font-semibold text-flame-600 mb-2">
                    📊 Forecast During Your Cook Window
                  </p>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Temperature Range</span>
                  <span className="font-medium">
                    {Math.min(...weather.hourly.map(h => h.temperatureF)).toFixed(0)}°F - {Math.max(...weather.hourly.map(h => h.temperatureF)).toFixed(0)}°F
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Average Temp</span>
                  <span className="font-medium">
                    {(weather.hourly.reduce((sum, h) => sum + h.temperatureF, 0) / weather.hourly.length).toFixed(0)}°F
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Max Wind</span>
                  <span className="font-medium">
                    {Math.max(...weather.hourly.map(h => h.windMph)).toFixed(0)} mph
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Precipitation Probability</span>
                  <span className="font-medium">
                    {((weather.hourly.filter(h => h.precipitation).length / weather.hourly.length) * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-xs text-green-600 mt-3 italic">
                  ✓ Using {weather.hourly.length} hours of forecast data during your cook for accurate impact calculation.
                </p>
              </>
            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Temperature</span>
                  <span className="font-medium">{weather.current.temperatureF.toFixed(0)}°F</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Feels Like</span>
                  <span className="font-medium">{weather.current.feelsLikeF.toFixed(0)}°F</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Wind</span>
                  <span className="font-medium">{weather.current.windMph.toFixed(0)} mph</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-smoke-600">Precipitation</span>
                  <span className="font-medium">{weather.current.precipitation ? 'Yes' : 'No'}</span>
                </div>
                <p className="text-xs text-amber-600 mt-3 italic">
                  ⚠️ Using current conditions only. Forecast data not available for your cook window.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase Breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold text-smoke-900 mb-4">🔮 Phase Predictions</h3>
        <div className="space-y-3">
          <div className="border-b border-smoke-200 pb-2">
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Heat-up</span>
              <span className="font-medium">{estimate.phases.heatUp.durationHours.toFixed(1)}h</span>
            </div>
            <p className="text-xs text-smoke-500 mt-1">{estimate.phases.heatUp.explanation}</p>
          </div>
          {estimate.phases.stall.durationHours > 0 && (
            <div className="border-b border-smoke-200 pb-2">
              <div className="flex justify-between text-sm">
                <span className="text-smoke-600">Stall</span>
                <span className="font-medium">{estimate.phases.stall.durationHours.toFixed(1)}h</span>
              </div>
              <p className="text-xs text-smoke-500 mt-1">{estimate.phases.stall.explanation}</p>
            </div>
          )}
          <div className="border-b border-smoke-200 pb-2">
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Post-stall</span>
              <span className="font-medium">{estimate.phases.postStall.durationHours.toFixed(1)}h</span>
            </div>
            <p className="text-xs text-smoke-500 mt-1">{estimate.phases.postStall.explanation}</p>
          </div>
          <div className="pt-2">
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Rest</span>
              <span className="font-medium">{estimate.phases.rest.durationHours.toFixed(1)}h</span>
            </div>
            <p className="text-xs text-smoke-500 mt-1">{estimate.phases.rest.explanation}</p>
          </div>
        </div>
      </div>

      {/* Ready Schedule */}
      {estimate.perPiece.length > 1 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-smoke-900 mb-4">📅 Ready Schedule</h3>
          
          {/* Current Plan Section */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-smoke-800 mb-2">Your Current Plan</h4>
            <p className="text-xs text-smoke-600 mb-3">Based on {estimate.perPiece[0]?.restHours || 0}h rest for all pieces</p>
            <div className="space-y-3">
              {estimate.perPiece.map((piece) => (
                <div key={piece.id} className="border-l-2 border-smoke-400 pl-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-smoke-900">
                        {piece.finishesBeforeLargest ? '⏳ ' : '✓ '}
                        Piece {piece.id}
                        <span className="text-smoke-600 ml-2 font-normal">({piece.weight} lbs)</span>
                      </div>
                      <div className="text-xs text-smoke-600 mt-1">
                        Cook {piece.cookTimeHours.toFixed(1)}h + Rest {piece.restHours}h
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-smoke-900">
                        {piece.readyTime ? 
                          format(new Date(piece.readyTime), 'h:mm a') : 
                          'N/A'
                        }
                      </div>
                      {piece.holdHours > 0 && (
                        <div className={`text-xs mt-1 font-medium ${
                          piece.holdHours > 4 ? 'text-red-600' : 
                          piece.holdHours > 2 ? 'text-amber-600' : 
                          'text-green-600'
                        }`}>
                          +{piece.holdHours.toFixed(1)}h hold
                          {piece.holdHours > 4 && ' ⚠️'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {estimate.perPiece.some(p => p.holdHours > 2) && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                ⚠️ <strong>Safety Concern:</strong> Some pieces require {'>'}2h warm hold. See recommended schedule below.
              </div>
            )}
          </div>

          {/* Recommended Schedule Section */}
          {estimate.recommendedSchedule && estimate.recommendedSchedule.some(p => p.adjustmentReason) && (
            <div className="border-t border-smoke-200 pt-4">
              <h4 className="text-md font-semibold text-green-800 mb-2">💚 Recommended Schedule</h4>
              <p className="text-xs text-smoke-600 mb-3">Adjusted for food safety (max 2h warm hold, max 2.5h rest)</p>
              <div className="space-y-3">
                {estimate.recommendedSchedule.map((piece) => (
                  <div key={piece.id} className="border-l-2 border-green-500 pl-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-smoke-900">
                          {piece.finishesBeforeLargest ? '⏳ ' : '✓ '}
                          Piece {piece.id}
                          <span className="text-smoke-600 ml-2 font-normal">({piece.weight} lbs)</span>
                        </div>
                        <div className="text-xs text-smoke-600 mt-1">
                          Cook {piece.cookTimeHours.toFixed(1)}h + Rest {piece.restHours}h
                        </div>
                        {piece.adjustmentReason && (
                          <div className="text-xs text-green-700 mt-1 italic">
                            💡 {piece.adjustmentReason}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-smoke-900">
                          {piece.readyTime ? 
                            format(new Date(piece.readyTime), 'h:mm a') : 
                            'N/A'
                          }
                        </div>
                        {piece.holdHours > 0 && (
                          <div className={`text-xs mt-1 font-medium ${
                            piece.holdHours > 4 ? 'text-red-600' : 
                            piece.holdHours > 2 ? 'text-amber-600' : 
                            'text-green-600'
                          }`}>
                            +{piece.holdHours.toFixed(1)}h hold
                            {piece.holdHours > 2 && ' ⚠️'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-xs text-smoke-500 mt-4 italic border-t border-smoke-200 pt-3">
            ⏳ = Ready early, needs extended rest or warm hold | ✓ = Largest piece, sets timeline
          </p>
        </div>
      )}
      
      {/* Holding Window */}
      {estimate.holdingWindow && (
        <div className="card bg-blue-50 border border-blue-200">
          <h3 className="text-lg font-semibold text-smoke-900 mb-4">⏰ Flexible Start Window</h3>
          <div className="space-y-2 mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Earliest Safe Start</span>
              <span className="font-medium">{format(new Date(estimate.holdingWindow.earliestSafeStart), 'EEE h:mm a')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Recommended Start</span>
              <span className="font-medium text-flame-600">{format(new Date(estimate.holdingWindow.recommendedStart), 'EEE h:mm a')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Latest Safe Start</span>
              <span className="font-medium">{format(new Date(estimate.holdingWindow.latestSafeStart), 'EEE h:mm a')}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-blue-300 pt-2">
              <span className="text-smoke-600 font-semibold">Total Flexibility</span>
              <span className="font-semibold text-green-600">{estimate.holdingWindow.totalFlexibilityHours}h window</span>
            </div>
          </div>
          <p className="text-xs text-smoke-600 italic">{estimate.holdingWindow.explanation}</p>
        </div>
      )}
      
      {/* Summary Breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold text-smoke-900 mb-4">⏱️ Cook Time Breakdown</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Baseline Cook Time</span>
            <span className="font-medium">{estimate.summary.baselineHours.toFixed(1)}h</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Weather Adjustment</span>
            <span className="font-medium text-flame-600">
              {estimate.summary.weatherAdjustmentHours > 0 ? '+' : ''}{estimate.summary.weatherAdjustmentHours.toFixed(1)}h
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Strategy Adjustment</span>
            <span className="font-medium text-flame-600">
              {estimate.summary.strategyAdjustmentHours > 0 ? '+' : ''}{estimate.summary.strategyAdjustmentHours.toFixed(1)}h
            </span>
          </div>
          <div className="flex justify-between text-sm border-t border-smoke-200 pt-2 font-semibold">
            <span className="text-smoke-900">Total Cook Time</span>
            <span className="text-smoke-900">{estimate.summary.totalCookTimeHours.toFixed(1)}h</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Rest Time</span>
            <span className="font-medium">{estimate.phases.rest.durationHours.toFixed(1)}h</span>
          </div>
          <div className="flex justify-between text-sm border-t border-smoke-200 pt-2 font-bold">
            <span className="text-smoke-900">Total with Rest</span>
            <span className="text-smoke-900">{estimate.summary.totalTimeIncludingRestHours.toFixed(1)}h</span>
          </div>
        </div>
      </div>
      
      {/* Impact Factors */}
      <div className="card">
        <h3 className="text-lg font-semibold text-smoke-900 mb-4">🌡️ Impact Factors</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Smoker Efficiency</span>
            <span className="font-medium">{(estimate.factors.smokerEfficiency * 100).toFixed(0)}%</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Weather Impact</span>
            <span className="font-medium">{estimate.factors.weatherImpact.toFixed(3)}x</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Wind Impact</span>
            <span className="font-medium">{estimate.factors.windImpact.toFixed(3)}x</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Precipitation Impact</span>
            <span className="font-medium">{estimate.factors.precipitationImpact.toFixed(3)}x</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Spacing Factor</span>
            <span className="font-medium text-flame-600">{estimate.factors.spacingFactor.toFixed(2)}x</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-smoke-600">Thermal Load Factor</span>
            <span className="font-medium">{estimate.factors.thermalLoadFactor.toFixed(3)}x</span>
          </div>
          {estimate.factors.panFactor > 1.0 && (
            <div className="flex justify-between text-sm">
              <span className="text-smoke-600">Pan / Container Factor</span>
              <span className="font-medium text-flame-600">{estimate.factors.panFactor.toFixed(3)}x</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Warnings */}
      {estimate.warnings.length > 0 && (
        <div className="card bg-amber-50 border border-amber-300">
          <h3 className="text-lg font-semibold text-amber-900 mb-3">⚠️ Warnings</h3>
          <ul className="space-y-2">
            {estimate.warnings.map((warning, idx) => (
              <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5"></span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Recommendations */}
      {estimate.recommendations.length > 0 && (
        <div className="card bg-green-50 border border-green-200">
          <h3 className="text-lg font-semibold text-green-900 mb-3">💡 Recommendations</h3>
          <ul className="space-y-2">
            {estimate.recommendations.map((rec, idx) => (
              <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Food Safety Disclaimer */}
      <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 text-2xl">⚠️</div>
          <div>
            <h4 className="font-bold text-yellow-900 mb-1">FOOD SAFETY DISCLAIMER</h4>
            <p className="text-sm text-yellow-800 leading-relaxed">
              These calculations are <strong>estimates only</strong> based on typical conditions. 
              Cook times vary significantly based on your specific equipment, environment, and meat. 
              <strong className="block mt-1">
                Always use a calibrated meat thermometer to verify internal temperature.
              </strong>
              Never rely solely on time estimates. Follow FDA food safety guidelines for proper handling, 
              cooking, and storage of meat products.
            </p>
          </div>
        </div>
      </div>

      {/* Download PDF */}
      <button onClick={downloadPDF} className="btn-primary w-full flex items-center justify-center gap-2">
        <Download className="w-5 h-5" />
        Download Full Cook Plan (PDF)
      </button>

      {/* Start Over */}
      <button
        onClick={() => window.location.reload()}
        className="btn-secondary w-full"
      >
        Start New Cook Plan
      </button>
    </div>
  );
}
